import { ADDRESS_HOLD_PREFIX, DEFERRED_HOLD_PREFIX, holdKind, type HoldKind } from "@logiful/domain";
import { msg, t } from "@/lib/i18n";
import { serverText } from "@/lib/server-text";

/**
 * Zurückgehaltene Aufträge in der Liste: statt „Zurückgehalten“ + abgeschnittenem Satz eine kurze Pille mit der Art
 * des Grundes und darunter das Wesentliche in Klartext (Artikelname statt EAN). Der volle Grund steht im Tooltip.
 * Absichtlich gewartete Aufträge (Freigabetermin, Testbetrieb, Produktion, Umstellung) sind neutral, nicht gelb.
 */
const LABEL: Record<HoldKind, string> = {
  backorder: msg("Wartet auf Ware"),
  address: msg("Adresse prüfen"),
  address_logiful: msg("Adresse in Klärung"),
  address_incomplete: msg("Adresse unvollständig"),
  payment: msg("Zahlung offen"),
  unknown_sku: msg("Artikel unbekannt"),
  master_data: msg("Stammdaten fehlen"),
  insert_stock: msg("Beilage fehlt"),
  batch: msg("Charge fehlt"),
  b2b_requirement: msg("Vorgabe offen"),
  fba_labels: msg("Etiketten fehlen"),
  export_document: msg("Ausfuhrdokument fehlt"),
  test_mode: msg("Testbetrieb"),
  production: msg("Wartet auf Produktion"),
  deferred: msg("Freigabetermin"),
  billing: msg("Pausiert"),
  migration: msg("Umstellung läuft"),
  recalled: msg("Zurückgeholt"),
  rule: msg("Regel greift"),
};
const INTENDED = new Set<HoldKind>(["test_mode", "production", "deferred", "migration", "recalled"]);

export interface HoldView {
  label: string;
  tone: "warning" | "neutral" | "danger";
  /** das Wesentliche in einer Zeile (oder null) */
  detail: string | null;
  /** weitere Gründe neben dem ersten */
  more: number;
  /** alle Gründe im Wortlaut (Tooltip) */
  full: string;
}

const clip = (s: string, n = 32) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);

/** „Fehlbestand: A (benötigt 2, frei 0), B (laut Lager nicht verfügbar)“ → „Trolleytasche · 2 fehlen (+1 Artikel)“ */
function shortageDetail(reason: string, nameOf: (sku: string) => string): string | null {
  const items = [...reason.slice("Fehlbestand:".length).matchAll(/\s*([^,(]+?)\s*\((?:benötigt (\d+), frei (-?\d+)|laut Lager nicht verfügbar)\)/g)];
  if (!items.length) return null;
  const [, sku, needed, free] = items[0]!;
  const name = clip(nameOf(sku!.trim()), 28);
  const head = needed != null ? `${name} · ${t("{n} fehlen", { n: Number(needed) - Math.max(0, Number(free)) })}` : `${name} · ${t("im Lager nicht verfügbar")}`;
  return items.length > 1 ? `${head} ${t("(+{n} Artikel)", { n: items.length - 1 })}` : head;
}

/** „Unbekannte Artikel: A, B“ → Namen statt SKU, soweit bekannt */
function skuListDetail(rest: string, nameOf: (sku: string) => string): string | null {
  const list = rest.replace(/^[:\s–-]+/, "").split(/,\s*/).filter(Boolean);
  if (!list.length) return null;
  const head = clip(nameOf(list[0]!.trim()), 28);
  return list.length > 1 ? `${head} ${t("(+{n} Artikel)", { n: list.length - 1 })}` : head;
}

export function holdView(reasons: string[], lines: { sku: string; name: string }[] = []): HoldView | null {
  if (!reasons.length) return null;
  const names = new Map(lines.map((l) => [l.sku, l.name]));
  const nameOf = (sku: string) => names.get(sku) ?? sku;
  const first = reasons[0]!;
  const kind = holdKind(first);
  let label = LABEL[kind];
  let detail: string | null = null;
  switch (kind) {
    case "backorder":
      detail = shortageDetail(first, nameOf);
      break;
    case "address":
      detail = clip(serverText(first.slice(ADDRESS_HOLD_PREFIX.length)), 40);
      break;
    case "address_logiful":
      detail = t("Logiful klärt die Adresse");
      break;
    case "unknown_sku":
    case "master_data":
    case "insert_stock":
    case "batch": {
      const i = first.indexOf(":");
      detail = i >= 0 ? skuListDetail(first.slice(i + 1), nameOf) : null;
      break;
    }
    case "export_document":
      if (first.includes("wartet auf Freigabe")) {
        label = msg("Ausfuhrdokument prüfen");
        detail = t("wartet auf Freigabe");
      } else detail = t("im Auftrag hochladen");
      break;
    case "deferred": {
      const m = /(\d{1,2}\.\d{1,2}\.\d{4})/.exec(first.slice(DEFERRED_HOLD_PREFIX.length));
      detail = m ? t("geht am {d} ans Lager", { d: m[1]! }) : null;
      break;
    }
    case "billing":
      detail = t("offene Rechnungen");
      break;
    case "recalled":
      detail = t("wartet auf Freigabe");
      break;
    case "rule":
      // eigener Regeltext des Kunden – unverändert
      detail = clip(first, 40);
      break;
    default:
      detail = null;
  }
  return {
    label: t(label),
    tone: kind === "billing" ? "danger" : INTENDED.has(kind) ? "neutral" : "warning",
    detail,
    more: reasons.length - 1,
    full: reasons.map(serverText).join("\n"),
  };
}
